import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Select, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";

import ColumnSettings from "../components/ColumnSettings";
import * as projectsApi from "../../../../core/data/redux/services/projectsApi";
import { fetchProjectByKey } from "../../../../core/data/redux/projects/thunks";
import {
  selectActiveCompany,
  selectBoards,
  selectCurrentProject,
  selectStatuses,
} from "../../../../core/data/redux/projects/selectors";
import { useProjectCapability } from "../../hooks/useProjectRole";
import { errMsg } from "../../helpers/errMsg.helper";
import { CONFIRM_MODAL_PROPS } from "../../helpers/modal.style";
import { notify } from "../../../../services/toast.config";

import type {
  Status,
  StatusCategory,
} from "../../../../core/data/redux/projects/types";
import type { AppDispatch } from "../../../../core/data/redux/store";

const BoardSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const project = useSelector(selectCurrentProject);
  const boards = useSelector(selectBoards);
  const statuses = useSelector(selectStatuses);
  const activeCompany = useSelector(selectActiveCompany);
  const canManage = useProjectCapability(project?.id, "UPDATE_PROJECT");

  const defaultBoard = useMemo(
    () => boards.find((b) => b.is_default) || boards[0] || null,
    [boards]
  );

  const [refreshing, setRefreshing] = useState(false);

  const refresh = useCallback(async () => {
    if (!project?.key) return;
    setRefreshing(true);
    try {
      await dispatch(fetchProjectByKey(project.key)).unwrap();
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, project?.key]);

  useEffect(() => {
    if (project && (!boards.length || !statuses.length)) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const boardStatuses = useMemo(
    () =>
      defaultBoard
        ? statuses
          .filter((s) => s.board_id === defaultBoard.id)
          .sort((a, b) => a.position - b.position)
        : [],
    [defaultBoard, statuses]
  );

  const handleReorder = useCallback(
    async (statusIds: number[]) => {
      if (!defaultBoard) return;
      try {
        await projectsApi.reorderStatuses(
          activeCompany.company_id,
          activeCompany.company_type,
          defaultBoard.id,
          statusIds
        );
        await refresh();
        notify.success("Columns reordered");
      } catch (e: any) {
        notify.error(errMsg(e, "Failed to reorder columns"));
        throw e;
      }
    },
    [activeCompany, defaultBoard, refresh]
  );

  const handleCreate = useCallback(
    async (payload: {
      name: string;
      category: StatusCategory;
      color: string;
    }) => {
      if (!defaultBoard) return;
      try {
        await projectsApi.createStatus(
          activeCompany.company_id,
          activeCompany.company_type,
          {
            board_id: defaultBoard.id,
            name: payload.name,
            category: payload.category,
            color: payload.color,
          }
        );
        await refresh();
        notify.success("Column added");
      } catch (e: any) {
        notify.error(errMsg(e, "Failed to add column"));
        throw e;
      }
    },
    [activeCompany, defaultBoard, refresh]
  );

  const handleUpdate = useCallback(
    async (
      statusId: number,
      payload: { name?: string; category?: StatusCategory; color?: string }
    ) => {
      try {
        await projectsApi.updateStatus(
          activeCompany.company_id,
          activeCompany.company_type,
          statusId,
          payload
        );
        await refresh();
        notify.success("Column updated");
      } catch (e: any) {
        notify.error(errMsg(e, "Failed to update column"));
        throw e;
      }
    },
    [activeCompany, refresh]
  );

  const handleDelete = useCallback(
    async (status: Status) => {
      const referencingCount = await tryDelete(
        activeCompany.company_id,
        activeCompany.company_type,
        status.id,
        null
      );
      if (referencingCount === 0) {
        await refresh();
        notify.success("Column deleted");
        return;
      }
      const remaining = boardStatuses.filter((s) => s.id !== status.id);
      if (remaining.length === 0) {
        notify.error("Cannot delete the last column on this board");
        return;
      }
      Modal.confirm({
        ...CONFIRM_MODAL_PROPS,
        title: `Move ${referencingCount} issue(s) before deleting`,
        content: (
          <ColumnPickerModalContent
            options={remaining}
            onPicked={async (targetId) => {
              try {
                await tryDelete(
                  activeCompany.company_id,
                  activeCompany.company_type,
                  status.id,
                  targetId
                );
                await refresh();
                notify.success("Column deleted, issues moved");
              } catch (e: any) {
                notify.error(errMsg(e, "Failed to delete column"));
              }
            }}
          />
        ),
        okButtonProps: { style: { display: "none" } },
        cancelText: "Close",
      });
    },
    [activeCompany, boardStatuses, refresh]
  );

  if (!project || !defaultBoard) {
    return (
      <div className="card border-0" style={{ borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
        <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{ width: "2.5rem", height: "2.5rem" }} />
            <p className="mt-3 text-muted mb-0">Loading board settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card border-0" style={{ borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
      <div className="card-body bg-white rounded-3">
        {refreshing && (<div style={{ padding: "8px 12px", background: "#eff6ff", borderRadius: 8, color: "#3b82f6", fontSize: 13, marginBottom: 12 }}><i className="ti ti-loader me-2" />Refreshing…</div>)}
        <ColumnSettings
          statuses={boardStatuses}
          canManage={canManage}
          onReorder={handleReorder}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
};

const tryDelete = async (
  companyId: string,
  companyType: string,
  statusId: number,
  moveToStatusId: number | null
): Promise<number> => {
  try {
    await projectsApi.deleteStatus(
      companyId,
      companyType,
      statusId,
      moveToStatusId
    );
    return 0;
  } catch (e: any) {
    const data = e?.response?.data || e?.data;
    const messageList = Array.isArray(data?.message) ? data.message : [];
    const referencing = messageList[0]?.data?.referencing_count;
    if (referencing && referencing > 0) {
      return referencing;
    }
    throw e;
  }
};

interface ColumnPickerModalContentProps {
  options: Status[];
  onPicked: (targetId: number) => Promise<void>;
}

const ColumnPickerModalContent: React.FC<ColumnPickerModalContentProps> = ({
  options,
  onPicked,
}) => {
  const [target, setTarget] = useState<number | null>(options[0]?.id || null);
  const [submitting, setSubmitting] = useState(false);
  return (
    <div className="d-flex flex-column gap-3 mt-2">
      <p className="mb-0">
        Choose a column to move existing issues into:
      </p>
      <Select
        style={{ width: "100%" }}
        value={target ?? undefined}
        onChange={(v) => setTarget(v ?? null)}
        options={options.map((o) => ({ value: o.id, label: o.name }))}
      />
      <div className="d-flex justify-content-end gap-2">
        <button
          type="button"
          className="btn btn-primary"
          disabled={submitting || target == null}
          onClick={async () => {
            if (target == null) return;
            setSubmitting(true);
            try {
              await onPicked(target);
              Modal.destroyAll();
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {submitting ? "Moving…" : "Move and Delete"}
        </button>
      </div>
    </div>
  );
};

export default BoardSettings;
